import { createContext, useContext } from 'react'
import { useProjects } from '../hooks/useProjects'
import { projectService } from '../services/projectService'
import type {
  CreateProjectData,
  Project,
  UpdateProjectData,
} from '../types/project'

interface ProjectContextType {
  projects: Project[]
  isLoading: boolean
  error: Error | null
  refreshProjects: () => Promise<void>
  createProject: (projectData: CreateProjectData) => Promise<Project>
  updateProject: (
    projectId: number,
    projectData: UpdateProjectData,
  ) => Promise<Project>
  getProject: (projectId: number) => Promise<Project>
  acceptProjectInvite: (
    projectId: string | number,
    inviteId: string,
  ) => Promise<{ success: boolean }>
  deleteProject: (projectId: number) => Promise<void>
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined)

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const projectsData = useProjects()

  const createProject = async (projectData: CreateProjectData) => {
    const response = await projectService.saveProject(projectData)
    await projectsData.refreshProjects()
    return response
  }

  const updateProject = async (
    projectId: number,
    projectData: UpdateProjectData,
  ): Promise<Project> => {
    const response = await projectService.updateProject(projectId, projectData)
    await projectsData.refreshProjects()
    return response
  }

  const getProject = async (projectId: number): Promise<Project> => {
    return await projectService.getProject(projectId)
  }

  const acceptProjectInvite = async (
    projectId: string | number,
    inviteId: string,
  ): Promise<{ success: boolean }> => {
    const response = await projectService.acceptProjectInvite(
      projectId,
      inviteId,
    )
    await projectsData.refreshProjects()
    return response
  }

  const deleteProject = async (projectId: number): Promise<void> => {
    await projectService.deleteProject(projectId)
    await projectsData.refreshProjects()
  }

  return (
    <ProjectContext.Provider
      value={{
        ...projectsData,
        createProject,
        updateProject,
        getProject,
        acceptProjectInvite,
        deleteProject,
      }}
    >
      {children}
    </ProjectContext.Provider>
  )
}

export const useProjectContext = () => {
  const context = useContext(ProjectContext)
  if (!context) {
    throw new Error('useProjectContext must be used within a ProjectProvider')
  }
  return context
}
